// Local-first storage for Teleprompter scripts. Works fully offline (installed
// PWA, airplane mode, no signal). Keyed per-user so multiple accounts on the
// same device don't see each other's drafts.
//
// Shape of each local script:
//   {
//     id: "local-<uuid>",
//     title: string,
//     content: string,
//     speed: number, font_size: number, line_spacing: number, mirror: boolean,
//     length_bytes: number,
//     created_at: ISO string, updated_at: ISO string,
//     source: "local",
//   }
//
// Cloud scripts (when synced down from the server) are mirrored into a separate
// cache key so the page still renders the list while offline.

const localKey = (userId) => `studict.teleprompter.local.${userId || "anon"}`;
const cloudCacheKey = (userId) => `studict.teleprompter.cloud.${userId || "anon"}`;

function safeRead(key) {
  try {
    const raw = localStorage.getItem(key);
    if (!raw) return [];
    const parsed = JSON.parse(raw);
    return Array.isArray(parsed) ? parsed : [];
  } catch {
    return [];
  }
}

function safeWrite(key, list) {
  try {
    localStorage.setItem(key, JSON.stringify(list));
    return true;
  } catch {
    return false;
  }
}

function genLocalId() {
  return `local-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`;
}

export function listLocalScripts(userId) {
  return safeRead(localKey(userId)).sort((a, b) => (b.updated_at || "").localeCompare(a.updated_at || ""));
}

export function saveLocalScript(userId, script) {
  const list = safeRead(localKey(userId));
  const now = new Date().toISOString();
  const content = script.content || "";
  const length_bytes = new Blob([content]).size;
  const idx = list.findIndex((s) => s.id === script.id);
  const next = {
    ...script,
    id: script.id || genLocalId(),
    source: "local",
    length_bytes,
    created_at: script.created_at || now,
    updated_at: now,
  };
  if (idx >= 0) list[idx] = next;
  else list.unshift(next);
  safeWrite(localKey(userId), list);
  return next;
}

export function deleteLocalScript(userId, id) {
  const list = safeRead(localKey(userId)).filter((s) => s.id !== id);
  safeWrite(localKey(userId), list);
}

export function cacheCloudScripts(userId, scripts) {
  const list = (Array.isArray(scripts) ? scripts : []).map((s) => ({ ...s, source: "cloud" }));
  safeWrite(cloudCacheKey(userId), list);
}

export function getCachedCloudScripts(userId) {
  return safeRead(cloudCacheKey(userId));
}

export function downloadScriptAsTxt(script) {
  try {
    const blob = new Blob([script.content || ""], { type: "text/plain;charset=utf-8" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    const safeTitle = (script.title || "script").replace(/[^a-z0-9-_ ]/gi, "_").slice(0, 80) || "script";
    a.download = `${safeTitle}.txt`;
    document.body.appendChild(a);
    a.click();
    a.remove();
    setTimeout(() => URL.revokeObjectURL(url), 1000);
    return true;
  } catch {
    return false;
  }
}
